import { Modal } from '@/components/modal/modal';
import { Checkbox } from '@/components/ui/checkbox';
import { Button, HStack, Stack } from '@chakra-ui/react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

export interface IntegrationCertificationCheckbox {
  key: string;
  label: string;
  /** When true, the confirm button stays disabled until the box is checked. Default: true. */
  required?: boolean;
}

interface IntegrationCertificationModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  description?: string;
  checkboxes: IntegrationCertificationCheckbox[];
  confirmLabel: string;
  isSubmitting?: boolean;
  onConfirm: (acceptedKeys: string[]) => void;
}

/**
 * Generic legal / compliance certification gate. Consumers (Drive archive,
 * CSV exports, etc.) inject their own copy and checkbox list. The confirm
 * button unlocks only when every required box is ticked.
 */
export const IntegrationCertificationModal = ({
  isOpen,
  onClose,
  title,
  description,
  checkboxes,
  confirmLabel,
  isSubmitting = false,
  onConfirm,
}: IntegrationCertificationModalProps) => {
  const { t } = useTranslation();
  const [accepted, setAccepted] = useState<Record<string, boolean>>({});

  const allRequiredAccepted = useMemo(
    () => checkboxes.every((cb) => (cb.required === false ? true : accepted[cb.key] === true)),
    [accepted, checkboxes]
  );

  const toggle = (key: string, value: boolean | 'indeterminate') => {
    setAccepted((prev) => ({ ...prev, [key]: value === true }));
  };

  const handleConfirm = () => {
    const acceptedKeys = Object.entries(accepted)
      .filter(([, v]) => v)
      .map(([k]) => k);
    onConfirm(acceptedKeys);
  };

  const handleClose = () => {
    setAccepted({});
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={title}
      description={description}
      maxWidth="lg"
      footer={
        <HStack justifyContent="flex-end" w="full">
          <Button variant="outline" onClick={handleClose} disabled={isSubmitting}>
            {t('common.cancel')}
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={!allRequiredAccepted || isSubmitting}
            loading={isSubmitting}
            colorPalette="gray"
          >
            {confirmLabel}
          </Button>
        </HStack>
      }
    >
      <Stack
        gap={0}
        border="1px solid"
        borderColor="gray.200"
        borderRadius="md"
        divideY="1px"
        divideColor="gray.200"
        bg="gray.50"
      >
        {checkboxes.map((cb) => (
          <Checkbox
            key={cb.key}
            p={3}
            alignItems="flex-start"
            checked={accepted[cb.key] === true}
            onCheckedChange={({ checked }) => toggle(cb.key, checked)}
          >
            {cb.label}
          </Checkbox>
        ))}
      </Stack>
    </Modal>
  );
};

export default IntegrationCertificationModal;
